import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { fetchIntervalQuestIfNeeded } from '@lootboy/lib/actions/DailyBoostActions';
import { fetchIntervalProgressIfNeeded } from '@lootboy/lib/actions/IntervalProgressActions';
import { useRefreshTimer } from '@lootboy/lib/hooks';
import { DAY_MS } from '@lootboy/lib/time';
import { NOOP } from '@lootboy/lib/utils';
import { getEndOfUTCDay, isSameUTCDay } from '@lootboy/lib/utils/dateAndTime';

import DailyRewardTimer from '~/components/DailyTasksView/DailyRewardTimer';

import refresh from '~/assets/images/daily/refresh@2x.png';

const intervalSelector = createSelector(
  ({ dailyBoostInterval }) => dailyBoostInterval,
  ({ intervalProgress }) => intervalProgress,
  (
    {
      interval,
      lastSuccessfulFetch: lastSuccessfulFetchInterval,
      notificationsEnabled,
    },
    {
      progress,
      lastSuccessfulFetch: lastSuccessfulFetchProgress,
      fetchingInProgressSince,
    },
  ) => {
    const { rewards: rewardsPlain = [] } = interval;
    const { rewardsClaimed } = progress;

    const rewards = rewardsPlain.map((reward) => ({ ...reward }));

    const returnObject = {
      interval,
      rewards,
      progress,
      notificationsEnabled,
    };
    if (
      !lastSuccessfulFetchInterval ||
      !lastSuccessfulFetchProgress ||
      fetchingInProgressSince
    ) {
      returnObject.loading = true;
      return returnObject;
    }

    rewardsClaimed.forEach((reward, index) => {
      rewards[index] = reward;
      rewards[index].collected = true;
    });

    return {
      interval,
      rewards,
      progress,
      notificationsEnabled,
    };
  },
);

export default function useInterval() {
  const dispatch = useDispatch();
  useRefreshTimer(60 * 60 * 1000 * 24);
  useEffect(() => {
    dispatch(fetchIntervalQuestIfNeeded()).catch(NOOP);
    dispatch(fetchIntervalProgressIfNeeded()).catch(NOOP);
  }, [dispatch]);

  const data = useSelector(intervalSelector);

  if (data.loading) {
    return data;
  }

  const {
    interval: { startDate },
    progress: { lastClaimAt, nextReward, currentProgress },
    rewards: orgRewards,
  } = data;
  let refreshIndex;
  const rewards = [...orgRewards];
  const endDate = getEndOfUTCDay(startDate, 6);
  data.interval.endDate = endDate;

  if (currentProgress < 7) {
    let property;
    const now = new Date();
    const currentReward = { ...rewards[currentProgress] };

    if (currentReward) {
      rewards[currentProgress] = currentReward;
      if (!isSameUTCDay(lastClaimAt, now)) {
        property = 'active';
        currentReward.nextReward = nextReward;
      } else if (now.getUTCDay() + 1 !== new Date(startDate).getUTCDay()) {
        property = 'next';
      }
      if (property) {
        currentReward[property] = true;
      }
    }
    const unclaimed = +(property === 'active');
    const remainingDays = Math.floor((endDate - getEndOfUTCDay()) / DAY_MS);
    refreshIndex = currentProgress + remainingDays + unclaimed;

    if (refreshIndex < 0) {
      refreshIndex = 0;
    }
    if (refreshIndex < 7) {
      rewards[refreshIndex] = {
        element: (
          <DailyRewardTimer
            date={endDate}
            translation="DailyView_reset_popup"
          />
        ),
        src: refresh,
      };
      for (let i = refreshIndex + 1; i < 7; i += 1) {
        rewards[i] = undefined;
      }
    }
  }

  return {
    ...data,
    rewards,
    refreshIndex,
  };
}
